/* eslint-disable owa-custom-rules/no-side-effects, owa-custom-rules/forbid-empty-import-statements-from-modules -- web components */
import {
    css,
    type ViewTemplate,
    html,
    repeat,
    when,
    FASTElement,
    attr,
    volatile,
    ref,
    observable,
} from '@microsoft/fast-element';
import '@fluentui/web-components/divider.js';
import '@fluentui/web-components/menu.js';
import '@fluentui/web-components/menu-list.js';
import '@fluentui/web-components/menu-item.js';
import { lazySetMailListRowsFlagState } from 'owa-mail-triage-action';
import {
    lazyShowFlagDateTimePicker,
    getSelectedFlagState,
    isCurrentSelectionDisabledForReminders,
} from 'owa-flag-menu';
import { CheckmarkFilled, ClockAlarmRegular, FlagOffRegular } from '@fluentui/react-icons/fonts';
import getFlagCompleteState, {
    FlagCompleteEnum,
} from 'owa-mail-triage-action/lib/actions/helpers/getFlagCompleteState';
import loc, { type ResourceId } from 'owa-localize';
import {
    flagCompleteToday,
    flagCompleteTomorrow,
    flagCompleteThisWeek,
    flagCompleteNextWeek,
    flagCompleteNoDate,
    flagTodayButtonDescContextMenu,
    flagTomorrowButtonDescContextMenu,
    flagThisWeekButtonDescContextMenu,
    flagNextWeekButtonDescContextMenu,
    flagNoDateButtonDescContextMenu,
    flagCompleteCustom,
    flagCustomButtonDescContextMenu,
    flagCompleteAddReminder,
    flagCompleteEditReminder,
    flagEditReminderButtonDescContextMenu,
    flagAddReminderButtonDescContextMenu,
    flagCompleteMarkComplete,
    flagMarkCompleteButtonDescContextMenu,
    flagClearFlagButtonDescContextMenu,
    flagCompleteClearFlag,
} from '../../components/FlagContextMenu.locstring.json';
import { isCapabilityEnabled } from 'owa-capabilities';
import { advancedFlagCapability } from 'owa-capabilities-definitions/lib/advancedFlagCapability';
import type { OwaDate } from 'owa-datetime';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import getListViewStore from 'owa-mail-list-store/lib/store/Store';
import type FlagType from 'owa-service/lib/contract/FlagType';
import { owaDate } from 'owa-datetime';
import { getUserTimeZone } from 'owa-session-store';

import {
    getRibbonIconModeColor,
    type RibbonIconColor,
} from 'owa-command-ribbon/lib/util/ribbonIconModeColors';
import { mobxObservableProperty, mobxObserver } from '../utils/mobxObserver';
import { iconBarRegistry } from '../customElementRegistry';
import type { Menu } from '@fluentui/web-components';

interface DeadlineMenuItemProperty {
    label: ResourceId;
    title: ResourceId;
    enumValue: FlagCompleteEnum;
}

const DEADLINE_PROPS: DeadlineMenuItemProperty[] = [
    {
        label: flagCompleteToday,
        enumValue: FlagCompleteEnum.today,
        title: flagTodayButtonDescContextMenu,
    },
    {
        label: flagCompleteTomorrow,
        enumValue: FlagCompleteEnum.tomorrow,
        title: flagTomorrowButtonDescContextMenu,
    },
    {
        label: flagCompleteThisWeek,
        enumValue: FlagCompleteEnum.thisWeek,
        title: flagThisWeekButtonDescContextMenu,
    },
    {
        label: flagCompleteNextWeek,
        enumValue: FlagCompleteEnum.nextWeek,
        title: flagNextWeekButtonDescContextMenu,
    },
    {
        label: flagCompleteNoDate,
        enumValue: FlagCompleteEnum.noDate,
        title: flagNoDateButtonDescContextMenu,
    },
];

function showFlagDateTimePicker(
    rowKey: string,
    tableViewId: string,
    shouldSetReminder?: boolean,
    currReminderDate?: OwaDate
) {
    lazyShowFlagDateTimePicker.importAndExecute(
        'Hover',
        rowKey,
        tableViewId,
        undefined,
        undefined /*window*/,
        shouldSetReminder,
        currReminderDate
    );
}

const template: ViewTemplate<FlagMenu> = html`<fluent-menu close-on-scroll ${ref('menu')}>
    <fluent-menu-list popover>
        ${repeat(
            DEADLINE_PROPS,
            html<DeadlineMenuItemProperty, FlagMenu>`
                <fluent-menu-item
                    role="menuitem"
                    checked="${x => getSelectedFlagState() == x.enumValue}"
                    tabindex="-1"
                    data-indent="1"
                    ?disabled="${(_, c) => c.parent.remindersAreDisabled}"
                    title="${x => loc(x.title)}"
                    @click="${(x, c) => {
                        lazySetMailListRowsFlagState.importAndExecute(
                            [c.parent.rowKey],
                            c.parent.tableViewId,
                            getFlagCompleteState(x.enumValue),
                            'Hover',
                            true
                        );
                        return true;
                    }}"
                >
                    ${x => loc(x.label)}
                </fluent-menu-item>
            `
        )}
        ${when(
            _ => isCapabilityEnabled(advancedFlagCapability),
            html<FlagMenu>` <fluent-menu-item
                role="menuitem"
                tabindex="-1"
                data-indent="1"
                ?disabled="${x => x.remindersAreDisabled}"
                title="${loc(flagCustomButtonDescContextMenu)}"
                @click="${x => {
                    showFlagDateTimePicker(x.rowKey, x.tableViewId);
                    return true;
                }}"
            >
                ${_ => loc(flagCompleteCustom)}
            </fluent-menu-item>`
        )}
        <fluent-divider></fluent-divider>
        <fluent-menu-item
            role="menuitem"
            tabindex="1"
            data-indent="1"
            ?disabled="${x => x.remindersAreDisabled}"
            title="${x =>
                loc(
                    x.flagType?.ReminderIsSet
                        ? flagEditReminderButtonDescContextMenu
                        : flagAddReminderButtonDescContextMenu
                )}"
            @click="${x => {
                const flagType = x.flagType;
                showFlagDateTimePicker(
                    x.rowKey,
                    x.tableViewId,
                    true /* shouldSetReminder */,
                    flagType?.ReminderIsSet
                        ? owaDate(getUserTimeZone(), flagType?.ReminderTime)
                        : undefined
                );
                return true;
            }}"
        >
            <span slot="start">${ClockAlarmRegular.codepoint}</span>
            ${x =>
                loc(x.flagType?.ReminderIsSet ? flagCompleteEditReminder : flagCompleteAddReminder)}
        </fluent-menu-item>
        <fluent-menu-item
            role="menuitem"
            tabindex="1"
            data-indent="1"
            ?disabled="${x => x.flagType?.FlagStatus == 'Complete'}"
            title="${loc(flagMarkCompleteButtonDescContextMenu)}"
            @click="${x => {
                lazySetMailListRowsFlagState.importAndExecute(
                    [x.rowKey],
                    x.tableViewId,
                    getFlagCompleteState(FlagCompleteEnum.complete),
                    'Hover'
                );
                return true;
            }}"
        >
            <span class="checkmark" slot="start">${CheckmarkFilled.codepoint}</span>
            ${_ => loc(flagCompleteMarkComplete)}
        </fluent-menu-item>
        <fluent-menu-item
            role="menuitem"
            tabindex="1"
            data-indent="1"
            ?disabled="${x => {
                const flagStatus = x.flagType?.FlagStatus;
                return flagStatus == 'NotFlagged' || flagStatus == null;
            }}"
            title="${loc(flagClearFlagButtonDescContextMenu)}"
            @click="${x => {
                lazySetMailListRowsFlagState.importAndExecute(
                    [x.rowKey],
                    x.tableViewId,
                    getFlagCompleteState(FlagCompleteEnum.clear),
                    'Hover'
                );
                return true;
            }}"
        >
            <span slot="start">${FlagOffRegular.codepoint}</span>
            ${_ => loc(flagCompleteClearFlag)}
        </fluent-menu-item>
    </fluent-menu-list></fluent-menu
>`;

const styles = css`
    .checkmark {
        color: ${getRibbonIconModeColor(4) ?? 'inherit'};
    }

    [slot='start'] {
        font-family: FluentSystemIcons;
    }
`;

@mobxObserver
export class FlagMenu extends FASTElement {
    @attr({ mode: 'boolean' })
    isComplete!: boolean;

    @attr({ mode: 'boolean' })
    isFlagged!: boolean;

    @attr
    rowKey!: string;

    @attr
    tableViewId!: string;

    @mobxObservableProperty
    @volatile
    get flagType(): FlagType | undefined {
        const tableView = getListViewStore().tableViews.get(this.tableViewId);
        return tableView && MailRowDataPropertyGetter.getFlagType(this.rowKey, tableView);
    }

    @mobxObservableProperty
    @volatile
    get remindersAreDisabled(): boolean {
        return isCurrentSelectionDisabledForReminders(undefined, this.rowKey, this.tableViewId);
    }

    @observable
    menu!: Menu;
}

FlagMenu.define({
    name: 'flag-menu',
    template,
    styles,
    registry: iconBarRegistry,
});

declare global {
    namespace JSX {
        interface IntrinsicElements {
            'flag-menu': React.DetailedHTMLProps<React.HTMLProps<HTMLElement>, FlagMenu> & {
                isComplete: boolean;
                isFlagged: boolean;
                rowKey: string;
                tableViewId: string;
            };
        }
    }
}
/* eslint-enable owa-custom-rules/no-side-effects, owa-custom-rules/forbid-empty-import-statements-from-modules */
